import React, { ChangeEvent } from "react";
import TextButton from "@components/ui/buttons/TextButton";
import RequiredFieldIndicator from "@components/ui/indicators/RequiredFieldIndicator";
import { useTranslation } from "react-i18next";
import { t_help } from "@i18n/usualTexts/t_help";

const PREFIX = "showPasswordRadio-For";

const ShowPassword: React.FC<{
  name: string;
  nameconfirm: string;
  align: string;
}> = ({ name, nameconfirm, align = "left" }) => {
  const { t } = useTranslation();
  const [checked, setChecked] = React.useState(false);

  const handleToggle = () => {
    const newChecked = !checked;
    setChecked(newChecked);
    const type = newChecked ? "text" : "password";
    document.getElementById(`${name}`)?.setAttribute("type", type);
    if (nameconfirm) {
      document.getElementById(`${nameconfirm}`)?.setAttribute("type", type);
    }
  };

  return (
    <div className={`my-2 ${align === "right" ? "flex justify-end" : ""}`}>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <button
          type="button"
          role="checkbox"
          aria-checked={checked}
          onClick={handleToggle}
          className={[
            "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border-2 transition-all duration-200",
            "focus:outline-none focus:ring-2 focus:ring-[var(--checkbox-bg-checked)] focus:ring-offset-1",
            "hover:border-[var(--checkbox-border-hover)] hover:cursor-pointer",
            checked
              ? "border-transparent bg-[var(--checkbox-bg-checked)] text-[var(--checkbox-icon-color)] hover:bg-[var(--checkbox-bg-checked-hover)]"
              : "border-[var(--checkbox-border)] bg-[var(--checkbox-bg)]",
          ].filter(Boolean).join(" ")}
        >
          {checked && (
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
        {t(t_help.showPasswordCheck)}
      </label>
    </div>
  );
};

/** Props for the standard form text input field. */
export interface FormFieldProps {
  name: string;
  nameconfirm?: string;
  type?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  value: string;
  autofocus?: boolean;
  immediateValidation?: boolean;
  errors?: Set<string>;
  required?: boolean;
  submitted?: boolean;
  forgotPasswordText?: string;
  forgotPasswordAction?: () => void;
  toPresentErrors?: boolean;
  showPasswordButton?: boolean;
  placeholder?: string;
  inputMode?: "text" | "numeric" | "decimal" | "tel" | "search" | "email" | "url";
  pattern?: string;
  maxLength?: number;
  children: string;
}

const shouldAddRequiredError = (
  submitted: boolean,
  value: string,
  required: boolean
): boolean => {
  return submitted && !value && required;
};

const shouldRemoveRequiredError = (
  value: string,
  required: boolean
): boolean => {
  return (!!value && required) || !required;
};

const validateFieldErrors = (
  errors: Set<string>,
  submitted: boolean,
  value: string,
  required: boolean,
  requiredErrorMessage: string
): void => {
  if (shouldAddRequiredError(submitted, value, required)) {
    errors.clear();
    errors.add(requiredErrorMessage);
  } else if (shouldRemoveRequiredError(value, required)) {
    errors.delete(requiredErrorMessage);
  }
};

const renderErrorMessages = (
  errors: Set<string>,
  toPresentErrors: boolean,
  name: string,
  submitted: boolean
): React.ReactNode => {
  if (errors.size === 0 || !toPresentErrors) {
    return null;
  }

  return [...errors].map((error) => (
    <div
      key={error}
      id={`${name}-error`}
      data-testid="spanElement"
      className={`text-xs text-red-500 mt-1${submitted ? " animate-pulse" : ""}`}
      role="alert"
      aria-live="polite"
    >
      {error}
    </div>
  ));
};

const shouldShowPasswordSection = (
  forgotPasswordText: string | undefined,
  name: string,
  showPasswordButton: boolean
): boolean => {
  if (forgotPasswordText) return true;
  return name.includes("password") && (name.includes("confirm") || showPasswordButton);
};

const shouldShowPasswordSectionContent = (
  showForgotPassword: boolean,
  showPasswordToggle: boolean,
  showPlaceholder: boolean
): boolean => {
  return showForgotPassword || showPasswordToggle || showPlaceholder;
};

const renderForgotPasswordButton = (
  forgotPasswordText: string | undefined,
  forgotPasswordAction: (() => void) | undefined
): React.ReactNode => {
  if (!forgotPasswordText) return null;

  return (
    <div className="flex items-center">
      <TextButton text={forgotPasswordText} onClick={forgotPasswordAction} />
    </div>
  );
};

const renderPasswordToggle = (
  showPasswordToggle: boolean,
  showForgotPassword: boolean,
  name: string,
  nameconfirm: string
): React.ReactNode => {
  if (!showPasswordToggle) return null;

  return (
    <div className="flex items-center">
      <ShowPassword
        name={name}
        nameconfirm={nameconfirm}
        align={showForgotPassword ? "right" : "left"}
      />
    </div>
  );
};

const PasswordPlaceholder: React.FC<{ show: boolean }> = ({ show }) => {
  if (!show) return null;
  return <div className="invisible flex-1" />;
};

const renderPasswordSection = (
  forgotPasswordText: string | undefined,
  forgotPasswordAction: (() => void) | undefined,
  type: string,
  showPasswordButton: boolean,
  name: string,
  nameconfirm: string
): React.ReactNode => {
  const showForgotPassword = Boolean(forgotPasswordText);
  const showPasswordToggle = type === "password" && showPasswordButton;
  const showPlaceholder = !showForgotPassword && (!showPasswordButton || type !== "password");

  if (!shouldShowPasswordSectionContent(showForgotPassword, showPasswordToggle, showPlaceholder)) {
    return null;
  }

  return (
    <div className="flex py-2 min-h-[2.5rem]">
      {renderForgotPasswordButton(forgotPasswordText, forgotPasswordAction)}
      {renderPasswordToggle(showPasswordToggle, showForgotPassword, name, nameconfirm)}
      <PasswordPlaceholder show={showPlaceholder} />
    </div>
  );
};

const FieldPasswordSection: React.FC<{
  show: boolean;
  forgotPasswordText?: string;
  forgotPasswordAction?: () => void;
  type: string;
  showPasswordButton: boolean;
  name: string;
  nameconfirm: string;
}> = ({ show, forgotPasswordText, forgotPasswordAction, type, showPasswordButton, name, nameconfirm }) => {
  if (!show) return null;
  return (
    <>
      {renderPasswordSection(forgotPasswordText, forgotPasswordAction, type, showPasswordButton, name, nameconfirm)}
    </>
  );
};

const FIELD_DEFAULTS = {
  nameconfirm: "",
  type: "text",
  onChange: (() => {}) as (e: ChangeEvent<HTMLInputElement>) => void,
  autofocus: false,
  immediateValidation: true,
  required: false,
  submitted: false,
  forgotPasswordText: undefined as string | undefined,
  forgotPasswordAction: undefined as (() => void) | undefined,
  toPresentErrors: true,
  showPasswordButton: true,
};

function resolveFieldTextProps(props: FormFieldProps) {
  return {
    name: props.name,
    nameconfirm: props.nameconfirm ?? FIELD_DEFAULTS.nameconfirm,
    type: props.type ?? FIELD_DEFAULTS.type,
    onChange: props.onChange ?? FIELD_DEFAULTS.onChange,
    value: props.value,
    autofocus: props.autofocus ?? FIELD_DEFAULTS.autofocus,
  };
}

function resolveFieldStateProps(props: FormFieldProps) {
  return {
    immediateValidation: props.immediateValidation ?? FIELD_DEFAULTS.immediateValidation,
    required: props.required ?? FIELD_DEFAULTS.required,
    submitted: props.submitted ?? FIELD_DEFAULTS.submitted,
    toPresentErrors: props.toPresentErrors ?? FIELD_DEFAULTS.toPresentErrors,
  };
}

function resolveFieldValidationProps(props: FormFieldProps) {
  return {
    errors: props.errors ?? new Set<string>(),
    ...resolveFieldStateProps(props),
  };
}

function resolveFieldPasswordProps(props: FormFieldProps) {
  return {
    forgotPasswordText: props.forgotPasswordText ?? FIELD_DEFAULTS.forgotPasswordText,
    forgotPasswordAction: props.forgotPasswordAction ?? FIELD_DEFAULTS.forgotPasswordAction,
    showPasswordButton: props.showPasswordButton ?? FIELD_DEFAULTS.showPasswordButton,
  };
}

function resolveFieldProps(props: FormFieldProps) {
  return {
    ...resolveFieldTextProps(props),
    ...resolveFieldValidationProps(props),
    ...resolveFieldPasswordProps(props),
    children: props.children,
  };
}

const FormField: React.FC<FormFieldProps> = (props) => {
  const { name, nameconfirm, type, onChange, value, autofocus, immediateValidation, errors, required, submitted, forgotPasswordText, forgotPasswordAction, toPresentErrors, showPasswordButton, children } = resolveFieldProps(props);
  const { t } = useTranslation();
  const requiredErrorMessage = t(t_help.fieldOrSelect, {placeholder: children});

  validateFieldErrors(errors, submitted, value, required, requiredErrorMessage);

  const showSection = shouldShowPasswordSection(forgotPasswordText, name, showPasswordButton);
  const hasErrors = errors.size > 0;
  const errorBorderClass = hasErrors ? " border-red-500" : "";
  const errorDescribedBy = hasErrors ? `${name}-error` : undefined;

  return (
    <div>
      <label
        className={`block text-sm leading-tight mb-1.5 ${hasErrors ? "text-red-600" : "text-[var(--text-primary)]"}`}
        htmlFor={name}
      >
        {children}
        <RequiredFieldIndicator required={required} className="ml-0.5" />
      </label>

      <input
        className={`auth-input w-full h-10 px-3 py-2 text-sm bg-[var(--surface-primary)] text-[var(--text-primary)] border border-[var(--border-medium)] focus:outline-none focus:border-[var(--accent-primary)] placeholder:text-[var(--text-tertiary)] transition-colors duration-200${errorBorderClass}`}
        placeholder={props.placeholder ?? children}
        autoFocus={autofocus}
        required={required}
        data-immediate-validation={immediateValidation}
        name={name}
        id={name}
        type={type}
        value={value}
        onChange={onChange}
        aria-describedby={errorDescribedBy}
        inputMode={props.inputMode}
        pattern={props.pattern}
        maxLength={props.maxLength}
      />

      {renderErrorMessages(errors, toPresentErrors, name, submitted)}

      <FieldPasswordSection
        show={showSection}
        forgotPasswordText={forgotPasswordText}
        forgotPasswordAction={forgotPasswordAction}
        type={type}
        showPasswordButton={showPasswordButton}
        name={name}
        nameconfirm={nameconfirm}
      />
    </div>
  );
};

export default FormField;
